const MarketplaceReview = (props) => {
    return (
        <>
            <h1 className="d-title">Marketplace Review: Agentic RAG Product Intelligence Pipeline</h1>
            <hr />
            <p className="mt-4">
                The decision-making process before purchasing products is streamlined by this workflow through the intelligent aggregation and analysis of customer feedback.
            </p>
            <h3 className="mt-4">🔴 The Problem</h3>
            <p>
                Whether an individual consumer or a procurement team is involved, relying on scattered reviews across different platforms being manually read before a purchase is made is considered tedious. Significant time loss, information overload, and potentially biased or incomplete insights are caused by this manual process.
            </p>
            <h3 className="mt-4">🟢 The Solution</h3>
            <p>
                To solve this, an end-to-end pipeline was built by which the research phase is automated:
            </p>
            <ul>
                <li><strong>Automated Scraping:</strong> Product reviews are extracted directly from marketplace platforms.</li>
                <li><strong>Semantic Search:</strong> The reviews are embedded and stored into a vector database for fast, context-aware retrieval.</li>
                <li><strong>LLM Summarization:</strong> A local LLM is leveraged to accurately summarize various viewpoints, by which pros, cons, and overall consensus are highlighted.</li>
                <li><strong>User-Friendly Access:</strong> An intuitive Web UI and seamlessly deployed models are featured to provide an effortless experience for the end-user.</li>
            </ul>
            <h3 className="mt-4">📈 Results & Business Impact</h3>
            <p>
                Several distinct advantages are provided by the implementation of this workflow, particularly for companies and procurement teams:
            </p>
            <ul>
                <li><strong>Accelerated Decision-Making:</strong> The time spent on market research is reduced from hours to seconds by the provision of instant, digestible summaries of massive text volumes.</li>
                <li><strong>Data-Driven Procurement:</strong> Buyers are equipped with comprehensive sentiment analysis, ensuring that investments are made in high-quality, reliable products.</li>
                <li><strong>Competitive Intelligence:</strong> Competitor products can be quickly analyzed by businesses through the effortless aggregation of customer pain points and highly praised features.</li>
                <li><strong>Resource Efficiency:</strong> The heavy lifting of data gathering is automated, freeing up analysts so that strategic evaluation can be focused on rather than manual reading.</li>
            </ul>
            <h3 className="mt-4">🚀 Future Enhancements</h3>
            <p>
                This architecture can be further developed to:
            </p>
            <ul>
                <li>Structured input forms being provided to standardize how products are queried and reviewed.</li>
                <li>Capabilities being added to scrape, aggregate, and cross-reference reviews from multiple marketplaces simultaneously.</li>
                <li>Automated sentiment scoring and trend visualization dashboards being integrated.</li>
                <li>The RAG pipeline being optimized with advanced chunking strategies and hybrid search for even greater retrieval accuracy.</li>
            </ul>
            <p className="mt-4">
                An end-to-end, production-grade local <strong>Agentic RAG (Retrieval-Augmented Generation) Pipeline</strong> designed to ingest, embed, index, and reason over web-scraped product reviews. The system uses a LangChain to call agent that runs locally (Ollama Llama 3.2 3B) to dynamically query a local vector database (Qdrant)and deliver high-fidelity sentiment analysis and product intelligence.
            </p>
            <h3>System Architecture</h3>
            <img
                src="img/marketplaceReview/rag_pipeline_architecture.svg"
                alt="RAG Pipeline Architecture"
                style={{ objectFit: 'contain', maxWidth: '100%' }}
                className="rounded mx-auto d-block w-50 gambar_alur mb-4 mt-4"
            />
            <p>
                The pipeline is decoupled into four isolated phases:
            </p>
            <ul>
                <li><strong>Data Acquisition:</strong> Utilizes Bright Data Proxy API for resilient, anti-bot-resistant data extraction from high-security domains like Amazon product and review structures.</li>
                <li><strong>Knowledge Indexing:</strong> Processes the raw data into semantic text chunks, generates embeddings locally using Ollama (mxbai-embed-large), and indexes them in a local Qdrant Vector DB.</li>
                <li><strong>Agentic Reasoning:</strong> Implements an Agentic RAG pattern using LangChain Core and Llama 3.2 3B. The agent dynamically decides when local knowledge retrieval is required instead of relying on a basic sequence chain.</li>
                <li><strong>Presentation Layer:</strong> Provides the end-user interface for seamless interaction with the agent.</li>
            </ul>

            <h3>Chatbot UI</h3>
            <img
                src="img/marketplaceReview/streamlit_UI_chatbot.png"
                alt="Streamlit UI Chatbot"
                style={{ objectFit: 'contain', maxWidth: '100%' }}
                className="rounded mx-auto d-block w-50 gambar_alur mb-4 mt-4"
            />
            <p>
                The Presentation layer shows a chat interface using Streamlit. It provides AI responses and includes a R verified reference badges directly into the user interface, giving clear attribution to the retrieved context sources.
            </p>
            <p className="mt-4">
                <strong>Github:</strong> <a href="https://github.com/giovanniabel/marketplace-review-local-agentic-rag" target="_blank" rel="noreferrer">Marketplace-review-local-agentic-rag</a>
            </p>
        </>
    )
}

export default MarketplaceReview;
